import type { IHttpClient } from '@wix/yoshi-flow-editor';
import {
  listFirstAvailableTimeSlotForFulfillmentTypes,
  listAvailableTimeSlotsForDate,
  listFirstAvailableTimeSlotsForOperations,
  listAvailableDatesInRange,
  calculateFirstAvailableTimeSlotPerFulfillmentType,
  calculateAvailableTimeSlotsForDate,
  calculateFirstAvailableTimeSlotsPerOperation,
  calculateAvailableDatesInRange,
} from '@wix/ambassador-restaurants-operations-v1-operation/http';
import {
  queryFulfillmentMethods,
  listAvailableFulfillmentMethodsForAddress,
} from '@wix/ambassador-restaurants-v1-fulfillment-method/http';
import type { Address, DispatchType, Operation } from '../types/businessTypes';
import { context } from '../context/RootContext';
import { convertDate } from './utils/utils';
import {
  processFulfillmentTimeSlot,
  mergeASAPTimeSlots,
  processFulfillmentTimeSlotPerOperation,
  processTimeSlotForFulfillment,
  processFulfillmentTimeSlotByOperationList,
  mergeASAPTimeSlotsForFulfillment,
} from './utils/TimeSlotProcessor';
import type { ErrorHandlerPublicAPI } from '@wix/error-handler';
import { withCacheResponse } from './utils/withCacheResponse';
import type {
  CalculateAvailableTimeSlotsForDateRequest,
  CalculateAvailableTimeSlotsForDateResponse,
  ListAvailableTimeSlotsForDateRequest,
  ListAvailableTimeSlotsForDateResponse,
} from '@wix/ambassador-restaurants-operations-v1-operation/types';
import { DateTime } from 'luxon';

export class FulfillmentsClient {
  constructor(
    private httpClient: IHttpClient,
    private errorHandler: ErrorHandlerPublicAPI,
    private operations: Operation[],
    private selectedOperationId?: string,
    private useCalculateFirstAvailableAPI?: boolean
  ) {}

  operationId = this.selectedOperationId ?? this.operations[0].id;

  get operationIds() {
    return this.operations.map(({ id }) => id);
  }

  get fulfillmentIds() {
    return this.operations.flatMap(({ fulfillmentIds }) => fulfillmentIds);
  }

  setOperationId = (operationId: string) => {
    this.operationId = operationId;
  };

  fetchAllFulfillments = async (fulfillmentIds?: string[]) => {
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const { data } = await withErrorHandler(
        () =>
          this.httpClient.request(
            queryFulfillmentMethods({
              query: {
                filter: { id: { $in: fulfillmentIds } },
              },
            })
          ),

        { errorCodesMap: {} }
      );

      return data?.fulfillmentMethods ?? [];
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
      context.pubsub.publish('onFetchFailed', { oloState: 'errorState' });
      return [];
    }
  };

  // #region private methods
  private async _fetchFirstAvailableTimeSlot(deliveryAddress?: Address) {
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const { data } = await withErrorHandler(
        () =>
          this.httpClient.request(
            listFirstAvailableTimeSlotForFulfillmentTypes({
              operationId: this.operationId,
              deliveryAddress,
            })
          ),
        { errorCodesMap: {} }
      );

      return data?.timeSlots?.map(processFulfillmentTimeSlot) ?? [];
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
      return [];
    }
  }

  private async _calculateFirstAvailableTimeSlot(deliveryAddress?: Address) {
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const { data } = await withErrorHandler(
        () =>
          this.httpClient.request(
            calculateFirstAvailableTimeSlotPerFulfillmentType({
              operationId: this.operationId,
              deliveryAddress,
            })
          ),
        { errorCodesMap: {} }
      );
      return data?.timeslotsPerFulfillmentType?.map(processTimeSlotForFulfillment) ?? [];
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
      return [];
    }
  }

  private async _fetchFirstAvailableTimeSlotByOperationList(deliveryAddress?: Address) {
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const { data } = await withErrorHandler(
        () =>
          this.httpClient.request(
            listFirstAvailableTimeSlotsForOperations({
              operationIds: this.operationIds,
              deliveryAddress,
            })
          ),

        { errorCodesMap: {} }
      );

      return new Map(data?.timeSlots?.map(processFulfillmentTimeSlotPerOperation));
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
    }
  }

  private async _calculateFirstAvailableTimeSlotByOperationList(deliveryAddress?: Address) {
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const { data } = await withErrorHandler(
        () =>
          this.httpClient.request(
            calculateFirstAvailableTimeSlotsPerOperation({
              operationIds: this.operationIds,
              deliveryAddress,
            })
          ),

        { errorCodesMap: {} }
      );

      return new Map(data?.timeSlotsPerOperation?.map(processFulfillmentTimeSlotByOperationList));
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
    }
  }

  private async _fetchAvailableDatesInRange({
    deliveryAddress,
    dispatchType,
    from,
    until,
    timezone,
  }: {
    deliveryAddress?: Address;
    dispatchType: DispatchType;
    from: Date;
    until: Date;
    timezone: string;
  }) {
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const { data } = await withErrorHandler(
        () =>
          this.httpClient.request(
            listAvailableDatesInRange({
              operationId: this.operationId,
              deliveryAddress,
              from: convertDate(from),
              until: convertDate(until),
            })
          ),
        { errorCodesMap: {} }
      );

      return (
        data?.availableDates
          ?.filter(
            ({ fulfilmentType }) => (fulfilmentType as unknown as DispatchType) === dispatchType
          )
          .map(
            ({ dates }) =>
              dates?.map(({ year, month, day }) =>
                DateTime.fromObject(
                  { year: year as number, month: month as number, day: day as number },
                  {
                    zone: timezone,
                  }
                )
              ) ?? []
          )
          .flat() ?? []
      );
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
    }
  }

  private async _calculateAvailableDatesInRange({
    deliveryAddress,
    dispatchType,
    from,
    until,
    timezone,
  }: {
    deliveryAddress?: Address;
    dispatchType: DispatchType;
    from: Date;
    until: Date;
    timezone: string;
  }) {
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const { data } = await withErrorHandler(
        () =>
          this.httpClient.request(
            calculateAvailableDatesInRange({
              operationId: this.operationId,
              deliveryAddress,
              from: convertDate(from),
              until: convertDate(until),
            })
          ),
        { errorCodesMap: {} }
      );

      return (
        data?.availableDatesPerFulfillmentType
          ?.filter(
            ({ fulfilmentType }) => (fulfilmentType as unknown as DispatchType) === dispatchType
          )
          .map(
            ({ dates }) =>
              dates?.map(({ year, month, day }) =>
                DateTime.fromObject(
                  { year: year as number, month: month as number, day: day as number },
                  {
                    zone: timezone,
                  }
                )
              ) ?? []
          )
          .flat() ?? []
      );
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
    }
  }

  private async _calculateAvailableTimeSlotsForDate({
    deliveryAddress,
    dispatchType,
    date,
    useCache = false,
  }: {
    deliveryAddress?: Address;
    dispatchType: DispatchType;
    date: Date;
    useCache?: boolean;
  }) {
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const { data } = await withErrorHandler(
        () =>
          useCache
            ? withCacheResponse<
                CalculateAvailableTimeSlotsForDateRequest,
                CalculateAvailableTimeSlotsForDateResponse
              >({
                httpClient: this.httpClient,
                fn: calculateAvailableTimeSlotsForDate,
                request: {
                  operationId: this.operationId,
                  deliveryAddress,
                  date: convertDate(date),
                },
              })
            : this.httpClient.request(
                calculateAvailableTimeSlotsForDate({
                  operationId: this.operationId,
                  deliveryAddress,
                  date: convertDate(date),
                })
              ),
        { errorCodesMap: {} }
      );

      const timeSlotsByDispatchType = (data?.timeslotsPerFulfillmentType ?? []).filter(
        (slot) => (slot.fulfilmentType as unknown as DispatchType) === dispatchType
      );

      return mergeASAPTimeSlotsForFulfillment(timeSlotsByDispatchType).map((slot) =>
        processTimeSlotForFulfillment(slot)
      );
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
    }
  }

  private async _fetchAvailableTimeSlotsForDate({
    deliveryAddress,
    dispatchType,
    date,
    useCache = false,
  }: {
    deliveryAddress?: Address;
    dispatchType: DispatchType;
    date: Date;
    useCache?: boolean;
  }) {
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const { data } = await withErrorHandler(
        () =>
          useCache
            ? withCacheResponse<
                ListAvailableTimeSlotsForDateRequest,
                ListAvailableTimeSlotsForDateResponse
              >({
                httpClient: this.httpClient,
                fn: listAvailableTimeSlotsForDate,
                request: {
                  operationId: this.operationId,
                  deliveryAddress,
                  date: convertDate(date),
                },
              })
            : this.httpClient.request(
                listAvailableTimeSlotsForDate({
                  operationId: this.operationId,
                  deliveryAddress,
                  date: convertDate(date),
                })
              ),
        { errorCodesMap: {} }
      );

      const timeSlotsByDispatchType = (data?.timeSlots ?? []).filter(
        (slot) => (slot.fulfilmentType as unknown as DispatchType) === dispatchType
      );

      return mergeASAPTimeSlots(timeSlotsByDispatchType).map((slot) =>
        processFulfillmentTimeSlot(slot)
      );
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
    }
  }

  // #endregion

  async fetchFirstAvailableTimeSlot(deliveryAddress?: Address) {
    return this.useCalculateFirstAvailableAPI
      ? this._calculateFirstAvailableTimeSlot(deliveryAddress)
      : this._fetchFirstAvailableTimeSlot(deliveryAddress);
  }

  async fetchFirstAvailableTimeSlotByOperationList(deliveryAddress?: Address) {
    return this.useCalculateFirstAvailableAPI
      ? this._calculateFirstAvailableTimeSlotByOperationList(deliveryAddress)
      : this._fetchFirstAvailableTimeSlotByOperationList(deliveryAddress);
  }

  async fetchAvailableDatesInRange({
    deliveryAddress,
    dispatchType,
    from,
    until,
    timezone,
  }: {
    deliveryAddress?: Address;
    dispatchType: DispatchType;
    from: Date;
    until: Date;
    timezone: string;
  }) {
    return this.useCalculateFirstAvailableAPI
      ? this._calculateAvailableDatesInRange({
          deliveryAddress,
          dispatchType,
          from,
          until,
          timezone,
        })
      : this._fetchAvailableDatesInRange({ deliveryAddress, dispatchType, from, until, timezone });
  }

  async fetchAvailableTimeSlotsForDate({
    deliveryAddress,
    dispatchType,
    date,
    useCache = false,
  }: {
    deliveryAddress?: Address;
    dispatchType: DispatchType;
    date: Date;
    useCache?: boolean;
  }) {
    return this.useCalculateFirstAvailableAPI
      ? this._calculateAvailableTimeSlotsForDate({
          deliveryAddress,
          dispatchType,
          date,
          useCache,
        })
      : this._fetchAvailableTimeSlotsForDate({
          deliveryAddress,
          dispatchType,
          date,
          useCache,
        });
  }

  async getAvailableFulfillmentsByAddress(address: Address) {
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const { data } = await withErrorHandler(
        () =>
          this.httpClient.request(
            listAvailableFulfillmentMethodsForAddress({
              address,
              fulfillmentMethodIds: this.fulfillmentIds,
            })
          ),
        { errorCodesMap: {} }
      );

      return data ?? undefined;
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
      return null;
    }
  }
}
